import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Network, Plus, Trash2, ArrowRightLeft, UserMinus, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CUSTOM = "__custom__";
const ZONES = ["North", "South", "West", "Exports"];

function SalespersonPicker({ salespeople, value, custom, onId, onCustom, testid }) {
  return (
    <div className="space-y-1.5">
      <Select value={value} onValueChange={onId}>
        <SelectTrigger className="h-9" data-testid={testid}><SelectValue placeholder="Select salesperson" /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="__none">— Unassigned —</SelectItem>
          {salespeople.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.dealer_count} dealer(s)</SelectItem>)}
          <SelectItem value={CUSTOM}>+ Type a different name…</SelectItem>
        </SelectContent>
      </Select>
      {value === CUSTOM && (
        <Input value={custom} onChange={(e) => onCustom(e.target.value)} placeholder="Salesperson name" className="h-9" data-testid={`${testid}-custom`} />
      )}
    </div>
  );
}

export default function DealerAssignment() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [delDealer, setDelDealer] = useState(null);
  const [newLogin, setNewLogin] = useState(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["dealer-assignment"],
    queryFn: async () => (await api.get("/dealers/assignment-data")).data,
  });
  const dealers = data?.dealers || [];
  const salespeople = data?.salespeople || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["dealer-assignment"] });

  const setSalesperson = async (dealer, spId, customName) => {
    setBusy(dealer.id);
    try {
      const payload = spId === CUSTOM ? { salesperson_name: customName || "" }
        : spId === "__none" ? {} : { salesperson_id: spId };
      await api.put(`/dealers/${dealer.id}/salesperson`, payload);
      toast.success(`Updated salesperson for ${dealer.name}`);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const deleteDealer = async () => {
    if (!delDealer) return;
    setBusy(delDealer.id);
    try {
      await api.delete(`/dealers/${delDealer.id}`);
      toast.success(`Deleted ${delDealer.name}${delDealer.has_login ? " (portal login disabled)" : ""}`);
      setDelDealer(null); refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="dealer-assignment-page">
      <PageHeader title="Dealer Assignment" subtitle="Tag dealers to salespeople, onboard new dealers, and reassign a book when someone leaves.">
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setReassignOpen(true)} className="gap-1.5" data-testid="reassign-btn"><ArrowRightLeft className="h-4 w-4" /> Reassign Book</Button>
          <Button variant="outline" onClick={() => setRemoveOpen(true)} className="gap-1.5 text-red-600 hover:text-red-700" data-testid="remove-sp-btn"><UserMinus className="h-4 w-4" /> Remove Salesperson</Button>
          <Button onClick={() => setAddOpen(true)} className="gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid="add-dealer-btn"><Plus className="h-4 w-4" /> Add Dealer</Button>
        </div>
      </PageHeader>

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Dealer", "Zone", "Login Email", "Tagged Salesperson", ""].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow></TableHeader>
              <TableBody>
                {dealers.map((d) => (
                  <DealerRow key={d.id} dealer={d} salespeople={salespeople} busy={busy === d.id}
                    onSet={setSalesperson} onDelete={() => setDelDealer(d)} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <AddDealerDialog open={addOpen} onOpenChange={setAddOpen} salespeople={salespeople}
        onCreated={(res) => { refresh(); if (res.login_email && res.temp_password) setNewLogin(res); }} />
      <ReassignDialog open={reassignOpen} onOpenChange={setReassignOpen} salespeople={salespeople} onDone={refresh} />
      <RemoveSalespersonDialog open={removeOpen} onOpenChange={setRemoveOpen} salespeople={salespeople} onDone={refresh} />

      <AlertDialog open={!!delDealer} onOpenChange={(v) => !v && setDelDealer(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delDealer?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The dealer master record will be removed{delDealer?.has_login ? " and their portal login will be disabled" : ""}. Their existing leads and opportunities are kept for the salesperson.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteDealer} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-dealer">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!newLogin} onOpenChange={(v) => { if (!v) { setNewLogin(null); setCopied(false); } }}>
        <DialogContent className="max-w-sm" data-testid="new-login-dialog">
          <DialogHeader><DialogTitle>Dealer login created</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Share these credentials with the dealer. They'll be asked to change the password on first login.</p>
          <div className="rounded-md bg-slate-50 border p-3 space-y-1 text-sm">
            <p><span className="text-slate-400">Email:</span> <span className="font-mono">{newLogin?.login_email}</span></p>
            <p><span className="text-slate-400">Password:</span> <span className="font-mono font-semibold">{newLogin?.temp_password}</span></p>
          </div>
          <DialogFooter>
            <Button onClick={() => { navigator.clipboard.writeText(`Email: ${newLogin.login_email}\nPassword: ${newLogin.temp_password}`); setCopied(true); }} className="gap-1.5" data-testid="copy-login-btn">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{copied ? "Copied" : "Copy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DealerRow({ dealer, salespeople, busy, onSet, onDelete }) {
  const [spId, setSpId] = useState(dealer.salesperson_id || "__none");
  const [custom, setCustom] = useState("");
  const changed = spId !== (dealer.salesperson_id || "__none") || (spId === CUSTOM && custom.trim());
  return (
    <TableRow data-testid={`dealer-row-${dealer.id}`}>
      <TableCell className="font-medium text-slate-800">{dealer.name}</TableCell>
      <TableCell className="text-sm text-slate-500">{dealer.zone}</TableCell>
      <TableCell className="text-xs font-mono text-slate-500">{dealer.login_email || (dealer.has_login ? "—" : <span className="text-amber-600">no login</span>)}</TableCell>
      <TableCell className="min-w-[240px]">
        <div className="flex items-center gap-2">
          <SalespersonPicker salespeople={salespeople} value={spId} custom={custom}
            onId={setSpId} onCustom={setCustom} testid={`dealer-sp-${dealer.id}`} />
          {changed && (
            <Button size="sm" disabled={busy} onClick={() => onSet(dealer, spId, custom)} className="h-9 bg-slate-900 hover:bg-slate-800" data-testid={`save-sp-${dealer.id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Button size="icon" variant="ghost" onClick={onDelete} className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-dealer-${dealer.id}`}><Trash2 className="h-4 w-4" /></Button>
      </TableCell>
    </TableRow>
  );
}

function AddDealerDialog({ open, onOpenChange, salespeople, onCreated }) {
  const [f, setF] = useState({ name: "", zone: "West", city: "", spId: "__none", custom: "", email: "", create_login: true });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Dealer name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(), zone: f.zone, city: f.city, email: f.email, create_login: f.create_login,
        ...(f.spId === CUSTOM ? { salesperson_name: f.custom } : f.spId !== "__none" ? { salesperson_id: f.spId } : {}),
      };
      const { data } = await api.post("/dealers/with-login", payload);
      toast.success(`Added ${f.name.trim()}`);
      onOpenChange(false);
      setF({ name: "", zone: "West", city: "", spId: "__none", custom: "", email: "", create_login: true });
      onCreated(data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-dealer-dialog">
        <DialogHeader><DialogTitle>Add Dealer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Dealer Name <span className="text-red-500">*</span></Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="h-9" data-testid="add-dealer-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Zone</Label>
              <Select value={f.zone} onValueChange={(v) => set("zone", v)}>
                <SelectTrigger className="h-9" data-testid="add-dealer-zone"><SelectValue /></SelectTrigger>
                <SelectContent>{ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">City</Label><Input value={f.city} onChange={(e) => set("city", e.target.value)} className="h-9" data-testid="add-dealer-city" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Tag Salesperson</Label>
            <SalespersonPicker salespeople={salespeople} value={f.spId} custom={f.custom} onId={(v) => set("spId", v)} onCustom={(v) => set("custom", v)} testid="add-dealer-sp" />
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Login Email <span className="text-slate-400 font-normal">(optional — auto-generated if blank)</span></Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="dealer@company.com" className="h-9" data-testid="add-dealer-email" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={f.create_login} onChange={(e) => set("create_login", e.target.checked)} data-testid="add-dealer-create-login" />
            Create a portal login for this dealer
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="add-dealer-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Add Dealer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReassignDialog({ open, onOpenChange, salespeople, onDone }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!from || !to || from === to) { toast.error("Pick two different salespeople"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/dealers/reassign-salesperson", { from_salesperson_id: from, to_salesperson_id: to });
      toast.success(`Moved ${data.reassigned} dealer(s) to ${data.to}`);
      onOpenChange(false); setFrom(""); setTo(""); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reassign-dialog">
        <DialogHeader><DialogTitle>Reassign Dealer Book</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Move all dealers tagged to one salesperson over to another.</p>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">From</Label>
            <Select value={from} onValueChange={setFrom}><SelectTrigger className="h-9" data-testid="reassign-from"><SelectValue placeholder="Salesperson leaving / handing over" /></SelectTrigger>
              <SelectContent className="max-h-72">{salespeople.filter((s) => s.dealer_count > 0).map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.dealer_count} dealer(s)</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">To</Label>
            <Select value={to} onValueChange={setTo}><SelectTrigger className="h-9" data-testid="reassign-to"><SelectValue placeholder="New owner" /></SelectTrigger>
              <SelectContent className="max-h-72">{salespeople.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="reassign-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Reassign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RemoveSalespersonDialog({ open, onOpenChange, salespeople, onDone }) {
  const [who, setWho] = useState("");
  const [to, setTo] = useState("");
  const [saving, setSaving] = useState(false);
  const sel = salespeople.find((s) => s.id === who);
  const needsReassign = sel && sel.dealer_count > 0;
  const submit = async () => {
    if (!who) { toast.error("Pick a salesperson"); return; }
    if (needsReassign && !to) { toast.error("Choose who takes over their dealers"); return; }
    setSaving(true);
    try {
      const { data } = await api.delete(`/dealers/salesperson/${who}`, { params: to ? { reassign_to: to } : {} });
      toast.success(`${data.deactivated} removed${data.reassigned ? `, ${data.reassigned} dealer(s) reassigned` : ""}`);
      onOpenChange(false); setWho(""); setTo(""); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="remove-sp-dialog">
        <DialogHeader><DialogTitle>Remove Salesperson</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Deactivates the salesperson's login. If they have tagged dealers, hand them to someone else first.</p>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Salesperson</Label>
            <Select value={who} onValueChange={(v) => { setWho(v); setTo(""); }}><SelectTrigger className="h-9" data-testid="remove-sp-select"><SelectValue placeholder="Who is leaving" /></SelectTrigger>
              <SelectContent className="max-h-72">{salespeople.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.dealer_count} dealer(s)</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {needsReassign && (
            <div className="space-y-1.5"><Label className="text-xs">Reassign their {sel.dealer_count} dealer(s) to</Label>
              <Select value={to} onValueChange={setTo}><SelectTrigger className="h-9" data-testid="remove-sp-reassign"><SelectValue placeholder="New owner" /></SelectTrigger>
                <SelectContent className="max-h-72">{salespeople.filter((s) => s.id !== who).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-red-600 hover:bg-red-700 gap-2" data-testid="remove-sp-confirm">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
